import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, ScanSearch, ExternalLink, Loader2, Eye, Globe,
  ChevronLeft, ChevronRight, Clock,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/i18n/TranslationContext';

const statusStyles = {
  completed: 'border-vrs-green/30 text-vrs-green bg-vrs-green/10',
  running: 'border-vrs-cyan/30 text-vrs-cyan bg-vrs-cyan/10',
  pending: 'border-vrs-amber/30 text-vrs-amber bg-vrs-amber/10',
  failed: 'border-vrs-red/30 text-vrs-red bg-vrs-red/10',
};

function formatDate(iso) {
  if (!iso) return '\u2014';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function getScoreColor(score) {
  if (score === null || score === undefined) return 'text-muted-foreground';
  if (score >= 80) return 'text-vrs-green';
  if (score >= 60) return 'text-vrs-amber';
  return 'text-vrs-red';
}

export default function ScansPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [scans, setScans] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  const fetchScans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/scans', { params: { limit: PAGE_SIZE, skip: page * PAGE_SIZE } });
      setScans(res.data.scans || []);
      setTotal(res.data.total || 0);
    } catch { setScans([]); }
    setLoading(false);
  }, [page]);

  useEffect(() => { fetchScans(); }, [fetchScans]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="scans-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">{t('scans')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('manage_scans')}</p>
        </div>
        <Button onClick={() => navigate('/scans/run')} data-testid="new-scan-button">
          <Plus className="mr-2 h-4 w-4" />{t('new_scan')}
        </Button>
      </div>

      <Card className="border-border/60">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-xs">{t('target_url')}</TableHead>
                <TableHead className="text-xs">{t('compliance_score')}</TableHead>
                <TableHead className="text-xs">{t('status')}</TableHead>
                <TableHead className="text-xs">{t('date_issued')}</TableHead>
                <TableHead className="text-xs w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
              ) : scans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-16">
                    <ScanSearch className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm font-medium text-foreground">{t('no_scans_yet')}</p>
                    <p className="text-xs text-muted-foreground mt-1">{t('start_first_scan')}</p>
                    <Button size="sm" className="mt-3" onClick={() => navigate('/scans/run')}>
                      <Plus className="mr-2 h-4 w-4" />{t('start_first_scan_btn')}
                    </Button>
                  </TableCell>
                </TableRow>
              ) : scans.map(scan => (
                <TableRow key={scan.scan_id} data-testid="scan-row" className="hover:bg-muted/20 cursor-pointer" onClick={() => navigate(`/scans/${scan.scan_id}`)}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Globe className="h-3 w-3 text-muted-foreground shrink-0" />
                      <div>
                        <p className="text-sm font-medium">{scan.target_url}</p>
                        {scan.page_title && <p className="text-[10px] text-muted-foreground">{scan.page_title}</p>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {scan.compliance_score !== null && scan.compliance_score !== undefined ? (
                      <span className={cn('text-sm font-bold font-mono', getScoreColor(scan.compliance_score))}>
                        {scan.compliance_score}%
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">\u2014</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cn('text-[10px] capitalize', statusStyles[scan.status] || statusStyles.pending)}>
                      {scan.status === 'running' && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                      {t(`status_${scan.status}`) || scan.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">{formatDate(scan.created_at)}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); navigate(`/scans/${scan.scan_id}`); }}>
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</p>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
